import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../services/supabaseClient';

// ============================================================================
// OFFLINE BYPASS MODE
// SECURITY: Only allowed on localhost to prevent exploitation
// ============================================================================
const OFFLINE_MODE_KEY = 'studio_jenial_offline_mode';
const OFFLINE_USER_KEY = 'studio_jenial_offline_user';

// SECURITY: Check if running on localhost
const isLocalhost = (): boolean => {
    const hostname = window.location?.hostname || '';
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.');
};

const createOfflineUser = (): User => ({
    id: 'offline-user-' + Date.now(),
    email: 'offline@local.dev',
    app_metadata: {},
    user_metadata: { full_name: 'Offline User', avatar_url: '' },
    aud: 'authenticated',
    created_at: new Date().toISOString(),
} as User);

const createOfflineSession = (user: User): Session => ({
    access_token: 'offline-token-' + Date.now(),
    refresh_token: 'offline-refresh',
    expires_in: 3600 * 24 * 365,
    expires_at: Date.now() / 1000 + 3600 * 24 * 365,
    token_type: 'bearer',
    user,
} as Session);

interface AuthContextType {
    user: User | null;
    session: Session | null;
    loading: boolean;
    isBetaTester: boolean;
    isAdmin: boolean;
    isOfflineMode: boolean;
    providerToken: string | null;
    signInWithGoogle: () => Promise<void>;
    signInOffline: () => void;
    signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [loading, setLoading] = useState(true);
    const [isBetaTester, setIsBetaTester] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isOfflineMode, setIsOfflineMode] = useState(false);

    useEffect(() => {
        // Check if we're in offline mode from previous session
        const wasOffline = localStorage.getItem(OFFLINE_MODE_KEY) === 'true';
        if (wasOffline) {
            const savedUser = localStorage.getItem(OFFLINE_USER_KEY);
            if (savedUser) {
                try {
                    const offlineUser = JSON.parse(savedUser) as User;
                    setUser(offlineUser);
                    setSession(createOfflineSession(offlineUser));
                    setIsOfflineMode(true);
                    // SECURITY: Only grant beta access on localhost offline mode
                    if (isLocalhost()) {
                        setIsBetaTester(true);
                    }
                    setLoading(false);
                    console.log('🔌 OFFLINE MODE: Restored from localStorage');
                    return;
                } catch (e) {
                    console.error('Failed to restore offline session:', e);
                }
            }
        }

        if (!supabase) {
            console.error('Supabase client not initialized.');
            // SECURITY: Only allow offline fallback on localhost
            if (isLocalhost()) {
                console.log('🔌 Activating offline fallback (localhost only)...');
                activateOfflineMode();
            } else {
                console.error('❌ Supabase unavailable in production - cannot proceed');
                setLoading(false);
            }
            return;
        }

        // Try to get session from Supabase with timeout
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Supabase timeout')), 5000)
        );

        Promise.race([sessionPromise, timeoutPromise])
            .then((result: any) => {
                const session = result?.data?.session;
                setSession(session);
                console.log('Auth Session Update:', {
                    hasSession: !!session,
                    hasProviderToken: !!session?.provider_token,
                    providerTokenLength: session?.provider_token?.length
                });
                setUser(session?.user ?? null);
                if (session?.user) {
                    checkBetaStatus(session.user.email);
                } else {
                    setLoading(false);
                }
            })
            .catch((error) => {
                console.warn('⚠️ Supabase unreachable:', error.message);
                // SECURITY: Only activate offline mode on localhost
                if (isLocalhost()) {
                    console.log('🔌 Activating OFFLINE MODE (localhost only)...');
                    activateOfflineMode();
                } else {
                    console.error('❌ Cannot authenticate - Supabase timeout in production');
                    setLoading(false);
                }
            });

        // Listen for auth changes (only if supabase is available)
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            if (isOfflineMode) return; // Ignore if in offline mode

            setSession(session);
            console.log('Auth Session Update:', {
                hasSession: !!session,
                hasProviderToken: !!session?.provider_token,
                providerTokenLength: session?.provider_token?.length
            });
            setUser(session?.user ?? null);
            if (session?.user) {
                checkBetaStatus(session.user.email);
            } else {
                setIsBetaTester(false);
                setIsAdmin(false);
                setLoading(false);
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    const activateOfflineMode = () => {
        // SECURITY: Double-check localhost before granting offline access
        if (!isLocalhost()) {
            console.error('❌ SECURITY: Offline mode blocked - not localhost');
            setLoading(false);
            return;
        }

        const offlineUser = createOfflineUser();
        const offlineSession = createOfflineSession(offlineUser);

        setUser(offlineUser);
        setSession(offlineSession);
        setIsOfflineMode(true);
        setIsBetaTester(true); // Grant beta access only on localhost
        // SECURITY: Do NOT grant admin by default, even on localhost
        setIsAdmin(false);
        setLoading(false);

        // Persist offline state
        localStorage.setItem(OFFLINE_MODE_KEY, 'true');
        localStorage.setItem(OFFLINE_USER_KEY, JSON.stringify(offlineUser));

        // Inject Development API Keys
        localStorage.setItem('studio_api_key', JSON.stringify('AIzaSyC7ppiKR6jfxuj-g0MEwwRk6j3Z4bro3xs'));
        localStorage.setItem('huggingface_api_key', JSON.stringify('2cc4e682-7d2a-4376-911b-bded15c78074:fffdd15ab974b3a680ca8a4ed0c41ab5'));
        localStorage.setItem('fal_ltx_api_key', JSON.stringify('2cc4e682-7d2a-4376-911b-bded15c78074:fffdd15ab974b3a680ca8a4ed0c41ab5'));

        // Bypass Onboarding for Dev
        localStorage.setItem('studio_jenial_onboarding_seen_v2', 'true');

        console.log('✅ OFFLINE MODE ACTIVATED - Local development access granted with injected keys');
    };

    const checkBetaStatus = async (email: string | undefined) => {
        if (!email || !supabase) {
            // SECURITY: Do NOT default to true - deny access when can't verify
            console.warn('Cannot verify beta status - no email or supabase');
            setIsBetaTester(false);
            setLoading(false);
            return;
        }

        try {
            // Check if user is in beta_testers table
            const { data, error } = await supabase
                .from('beta_testers')
                .select('email')
                .eq('email', email)
                .single();

            if (data) {
                setIsBetaTester(true);

                // P1 FIX: Check admin role from user_roles table (not email heuristics)
                try {
                    const { data: roleData } = await supabase
                        .from('user_roles')
                        .select('role')
                        .eq('email', email)
                        .eq('role', 'admin')
                        .single();

                    if (roleData) {
                        setIsAdmin(true);
                        console.log('[Auth] Admin role granted via user_roles table');
                    }
                } catch {
                    // No admin role - that's fine
                    setIsAdmin(false);
                }
            } else {
                setIsBetaTester(false);
            }
        } catch (error) {
            console.error('Error checking beta status:', error);
            // SECURITY: Do NOT default to true on error - deny access
            setIsBetaTester(false);
        } finally {
            setLoading(false);
        }
    };

    const signInWithGoogle = async () => {
        if (!supabase) {
            console.warn('Supabase unavailable, using offline mode');
            activateOfflineMode();
            return;
        }

        try {
            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    // SECURITY: Reduced scopes - only request what's needed
                    scopes: 'https://www.googleapis.com/auth/drive.file',
                    redirectTo: window.location.origin,
                    queryParams: {
                        access_type: 'offline',
                        prompt: 'consent',
                    },
                }
            });
            if (error) throw error;
        } catch (error) {
            console.error('Google sign-in failed, activating offline mode:', error);
            activateOfflineMode();
        }
    };

    const signInOffline = () => {
        // SECURITY: Block offline mode in production
        if (!isLocalhost()) {
            console.error('❌ SECURITY: Offline mode is only available in development (localhost)');
            return;
        }
        activateOfflineMode();
    };

    const signOut = async () => {
        if (!isOfflineMode && supabase) {
            try {
                await supabase.auth.signOut();
            } catch (e) {
                console.warn('Sign out from Supabase failed:', e);
            }
        }

        // Clear offline state
        localStorage.removeItem(OFFLINE_MODE_KEY);
        localStorage.removeItem(OFFLINE_USER_KEY);

        setUser(null);
        setSession(null);
        setIsBetaTester(false);
        setIsOfflineMode(false);
    };

    return (
        <AuthContext.Provider value={{
            user,
            session,
            loading,
            isBetaTester,
            isAdmin,
            isOfflineMode,
            providerToken: session?.provider_token ?? null,
            signInWithGoogle,
            signInOffline,
            signOut
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
